import React from 'react';
import {useTranslation} from 'react-i18next';
import {
    ArrowsClockwise, FileText, GoogleDriveLogo, Info, Notepad, SpeakerHigh, UsersThree, WarningCircle,
} from '../../../icons';
import {AudioNotePlayer} from '../../../audio/AudioNotePlayer';
import {DOC_SOURCE, DOC_STATUS, docErrorParts} from '../../memory/docConstants';
import {kindLabelKey} from '../../memory/contextPackConstants';
import {
    LIB_KIND, autoSyncLabel, connectionAutoSyncs, connectionAwaitsFirstRead, connectionIsBroken,
    libDateLabel, libDurationLabel, libKindOf, libSizeLabel,
} from './sourcesModel';

/**
 * Una fila de la biblioteca «Documentos y notas» — la `.dprow` del mockup.
 *
 * Reemplaza la fila de TABLA de la Memoria clásica (`MemoryDocumentsView`), que tenía cinco
 * columnas y no cabía: en el panel del Recorrido «Caracteres · Hechos · Acciones» quedaban
 * apretadas contra el borde y el nombre del documento —lo único por lo que alguien escanea la
 * lista— era la columna más angosta. Acá el nombre manda, y todo lo demás es una línea de
 * metadatos abajo.
 *
 * **El estado de un material fallido es DISCRETO** (punto 8 de la card): Pablo vio la lista «más
 * sucia» por las pruebas, y tenía razón. Discreto son tres cosas, y las tres se corrigieron después
 * del QA: nada de rojo pleno (lo original), la baldosa **acompaña** al glifo de alerta en vez de
 * quedarse con el verde de «bien», y el motivo ocupa **una** línea con elipsis — con seis fallidas
 * seguidas, dos líneas de gris repetidas seis veces eran lo que dominaba la lista.
 *
 * Los estados EN VUELO sí se anuncian con su fase, porque ahí la pregunta del usuario es «¿está
 * pasando algo?» y la respuesta tiene que estar en la fila.
 *
 * ⚠️ **El ícono habla del TIPO, no del origen.** Es lo que hace el mockup (`TIPO_SVGS`: documento,
 * nota, audio, reunión) y lo que mantiene coherente el filtro, donde un archivo de Drive cae en
 * «Documentos». La baldosa de Drive existe sólo para la fila de una conexión que todavía no generó
 * documento — ahí no hay tipo del que hablar. El origen de un documento se lee en sus metadatos.
 */

const KIND_ICON = {
    [LIB_KIND.DOC]: FileText,
    [LIB_KIND.NOTE]: Notepad,
    [LIB_KIND.AUDIO]: SpeakerHigh,
    [LIB_KIND.MEETING]: UsersThree,
};

const PHASE_KEY = {
    [DOC_STATUS.PENDING]: 'ia_booster.feed.ingest_queued',
    [DOC_STATUS.EXTRACTING]: 'ia_booster.feed.ingest_extracting',
};

// El separador de la línea de metadatos. Va como elemento y no como texto pegado a cada pedazo
// para que los que no se dibujan (un tamaño ausente, un tipo sin clasificar) no dejen un «·»
// suelto colgando.
const Meta = ({children}) => <span className="iab-src-meta-item">{children}</span>;

/**
 * El estado de una conexión de Drive y su acción, para las dos filas que pueden mostrarlo: la de
 * una conexión sin documento y la de un documento cuyo archivo perdió el acceso.
 *
 * **El motivo del backend NO se pinta crudo.** `last_sync_error` trae lo que devolvió Drive —
 * mensajes en inglés, códigos, a veces la dirección de mail de la cuenta que perdió el permiso —,
 * mientras que los fallos de DOCUMENTO ya pasan por `docErrorParts`. Acá se dice qué pasó con el
 * vocabulario del producto y el crudo queda en el `title`, para soporte.
 */
function ConnectionState({connection}) {
    const {t} = useTranslation();
    const key = `client_memory.connections.status.${(connection.status || '').toLowerCase()}`;
    return (
        <Meta>{t(key, {defaultValue: connection.status || ''})}</Meta>
    );
}

/**
 * «Actualizar» de una conexión de Drive, con su ⓘ. Drive se relee solo todos los días; el botón sirve
 * para no esperar, y eso lo dice el ⓘ: sin él, el botón a la vista se leía como «esto se sincroniza a
 * mano» (Hesta, 23/09: «¿los archivos de Drive quedan sincronizados?»).
 *
 * El ⓘ es HERMANO del botón, no hijo (review C-chicos-04): adentro, tocarlo en un teléfono —donde no
 * hay hover— pedía la relectura en vez de explicarla, y el cartel entraba al nombre accesible del
 * botón. Es un elemento enfocable con su texto como nombre (patrón de `FilterNotesInfoIcon`), y el
 * cartel es el `.iab-tip` oscuro del Booster (el design system prohíbe el `title` nativo), oculto al
 * lector de pantalla para que no se lea dos veces.
 */
function DriveRefreshButton({syncing, onSync}) {
    const {t} = useTranslation();
    const tip = t('client_memory.sources.refresh_tip');
    return (
        <span className="iab-src-refresh">
            <button type="button" className="iab-src-act" disabled={syncing} onClick={onSync}>
                <ArrowsClockwise size={13} aria-hidden="true"/>
                {syncing ? t('client_memory.connections.syncing') : t('ia_booster.sources.refresh')}
            </button>
            <span className="iab-src-info" tabIndex={0} role="img" aria-label={tip}>
                <Info size={13} aria-hidden="true"/>
                <span className="iab-tip" aria-hidden="true">{tip}</span>
            </span>
        </span>
    );
}

function ConnectionProblem({connection}) {
    const {t} = useTranslation();
    if (!connectionIsBroken(connection)) return null;
    return (
        <div className="iab-src-note -reason" title={connection.last_sync_error || undefined}>
            {t('ia_booster.sources.conn_broken')}
        </div>
    );
}

/** La fila de una conexión de Drive que todavía no tiene documento. */
function ConnectionRow({connection, syncing, canConnect, onSync, onReconnect}) {
    const {t} = useTranslation();
    const broken = connectionIsBroken(connection);
    return (
        <li className={`iab-src-row ${broken ? '-quiet' : ''}`}>
            <span className="iab-src-ic -drive" aria-hidden="true"><GoogleDriveLogo size={16}/></span>
            <div className="iab-src-main">
                <div className="iab-src-name">{connection.external_label}</div>
                <div className="iab-src-meta">
                    <Meta>{t('client_memory.doc_source.drive')}</Meta>
                    <ConnectionState connection={connection}/>
                    {/* Una conexión sin documento todavía no dio nada que leer: decirlo evita que
                        se lea como un material vacío. Recién conectada, la primera lectura está en
                        curso y la biblioteca vuelve a preguntar sola (`SourcesLibrary`): se dice
                        que está pasando algo, no que falta. Ya leída —su documento se borró desde
                        acá y el sync diario lo va a volver a traer—, no se dice nada: «Todavía sin
                        leer» contradecía al «última: hoy» de abajo (review C-chicos-06). */}
                    {connectionAwaitsFirstRead(connection) && (
                        <Meta>{t('client_memory.connections.syncing')}</Meta>
                    )}
                    {!connectionAwaitsFirstRead(connection) && !connection.last_synced_at && (
                        <Meta>{t('ia_booster.sources.conn_pending')}</Meta>
                    )}
                </div>
                {connectionAutoSyncs(connection) && (
                    <div className="iab-src-note">{autoSyncLabel(t, connection)}</div>
                )}
                <ConnectionProblem connection={connection}/>
            </div>
            <div className="iab-src-acts">
                {broken ? (
                    // Sin el Picker configurado, «Reconectar» no puede abrir nada: se dice que hay
                    // que reconectar, pero no se ofrece un botón que no hace nada.
                    canConnect && (
                        <button type="button" className="iab-src-act" onClick={onReconnect}>
                            {t('client_memory.connections.reconnect')}
                        </button>
                    )
                ) : (
                    <DriveRefreshButton syncing={syncing} onSync={onSync}/>
                )}
            </div>
        </li>
    );
}

export function SourceRow({
    entry,
    busy = false,
    syncing = false,
    canConnect = false,
    audio = null,
    onViewRaw,
    onRelabel,
    onDelete,
    onRetry,
    onReprocess,
    onRefreshUrl,
    onSyncConnection,
    onReconnect,
    onListen,
}) {
    const {t} = useTranslation();
    const doc = entry.document;

    if (!doc) {
        return (
            <ConnectionRow connection={entry.connection} syncing={syncing} canConnect={canConnect}
                           onSync={onSyncConnection} onReconnect={onReconnect}/>
        );
    }

    const kind = libKindOf(doc);
    const Icon = KIND_ICON[kind] || FileText;
    const stale = doc.stale === true;
    const failed = doc.status === DOC_STATUS.FAILED || stale;
    const ready = doc.status === DOC_STATUS.READY;
    const flying = !failed && (doc.status === DOC_STATUS.PENDING
        || doc.status === DOC_STATUS.EXTRACTING);
    const facts = doc.facts_extracted_count;
    // `facts_extracted_count` puede no venir (agencia en nivel MEDIO, o backend viejo): sólo se
    // dibuja la pastilla si el valor es un NÚMERO. Un `|| 0` diría «0 hechos» de todo, que es la
    // peor afirmación posible — dice que Dashbo leyó y no encontró nada.
    const knownFacts = typeof facts === 'number';
    const isAudio = kind === LIB_KIND.AUDIO;
    const isDrive = !!entry.connection;
    // Una conexión de Drive puede morir (token revocado, archivo en la papelera) SIN que se borre
    // el documento que ya trajo — el sync lo documenta explícitamente. Esa fila tiene que decir que
    // perdió el acceso y ofrecer el arreglo, no un «Actualizar» que encola un job que va a morir en
    // el mismo token: el `POST /sync/` devuelve `OK` sólo porque encoló, así que la pantalla
    // festejaría «Sincronizado» sobre algo que no puede funcionar.
    const driveBroken = connectionIsBroken(entry.connection);
    const isUrlDoc = doc.source === DOC_SOURCE.URL;
    const size = libSizeLabel(doc);
    const duration = libDurationLabel(doc);

    return (
        <li className={`iab-src-row ${failed || driveBroken ? '-quiet' : ''}`}>
            <span className={`iab-src-ic ${failed ? '-failed' : `-${kind}`}`} aria-hidden="true">
                {failed ? <WarningCircle size={16}/> : <Icon size={16}/>}
            </span>
            <div className="iab-src-main">
                <div className="iab-src-name">{doc.title}</div>
                <div className="iab-src-meta">
                    {/* La pastilla verde de los hechos va PRIMERA, como en el mockup: es la
                        respuesta a «¿esto sirvió de algo?», que es por lo que se abre la lista. */}
                    {ready && knownFacts && facts > 0 && (
                        <span className="iab-src-facts">
                            {/* Clave propia y no la de la lista de ingesta (`ingest_facts`): allá
                                el texto es «→ N hechos NUEVOS», que es cierto mientras el material
                                acaba de entrar y falso en una biblioteca donde la fila puede tener
                                seis meses. Por lo mismo el cero dice «Sin hechos» y no «Sin hechos
                                nuevos». */}
                            {t(facts === 1
                                ? 'ia_booster.sources.facts_one'
                                : 'ia_booster.sources.facts_many', {n: facts})}
                        </span>
                    )}
                    {ready && knownFacts && facts === 0 && (
                        <Meta>{t('ia_booster.sources.facts_none')}</Meta>
                    )}
                    {flying && (
                        <span className="iab-src-phase">
                            {t(PHASE_KEY[doc.status] || PHASE_KEY[DOC_STATUS.PENDING])}
                        </span>
                    )}
                    {/* El tipo con el que quedó clasificado, y la puerta para corregirlo: es lo
                        que decide para qué ítem del contexto cuenta este material. */}
                    <button type="button"
                            className={`iab-src-kind ${doc.document_kind ? '' : '-none'}`}
                            aria-label={`${t('client_memory.documents.change_kind')}: ${
                                doc.document_kind
                                    ? t(kindLabelKey(doc.document_kind))
                                    : t('client_memory.documents.kind_none')}`}
                            onClick={() => onRelabel(doc)}>
                        {doc.document_kind
                            ? t(kindLabelKey(doc.document_kind))
                            : t('client_memory.documents.kind_none')}
                    </button>
                    {/* El origen sólo cuando NO es lo obvio: un archivo de Drive y una página web
                        se distinguen del resto, un upload manual no agrega nada. */}
                    {isDrive && <Meta>{t('client_memory.doc_source.drive')}</Meta>}
                    {isDrive && driveBroken && <ConnectionState connection={entry.connection}/>}
                    {isUrlDoc && <Meta>{t('client_memory.doc_source.url')}</Meta>}
                    <Meta>{entry.date
                        ? libDateLabel(entry.date)
                        : t('ia_booster.sources.no_date')}</Meta>
                    {duration && <Meta>{duration}</Meta>}
                    {!duration && size && <Meta>{size}</Meta>}
                </div>
                {/* Que Drive se relee solo, y cuándo fue la última vez: es la pregunta que se hace
                    quien ve un archivo conectado (Hesta, 23/09). Renglón propio y no un ítem más de
                    los metadatos: la fecha de arriba es la del material, ésta la de la lectura. */}
                {isDrive && !failed && connectionAutoSyncs(entry.connection) && (
                    <div className="iab-src-note">{autoSyncLabel(t, entry.connection)}</div>
                )}
                {/* El motivo del fallo, en UNA línea atenuada y con elipsis: el texto entero queda
                    en el `title`, y el volcado del motor nunca va a pantalla (QA 2026-09-07,
                    QA-VIS-cola-06) — sigue detrás del `details`. */}
                {failed && (() => {
                    if (stale && !doc.summary_error) {
                        return (
                            <div className="iab-src-note -reason">
                                {t('client_memory.doc_stale')}
                            </div>
                        );
                    }
                    if (!doc.summary_error) return null;
                    const {message, technical} = docErrorParts(doc, t);
                    return (
                        <>
                            <div className="iab-src-note -reason" title={message}>{message}</div>
                            {technical && (
                                <details className="iab-src-note-detail">
                                    <summary>{t('client_memory.doc_error_detail')}</summary>
                                    <code>{technical}</code>
                                </details>
                            )}
                        </>
                    );
                })()}
                {isDrive && <ConnectionProblem connection={entry.connection}/>}
                {/* El reproductor se monta en la fila, debajo de los metadatos: una nota de audio
                    se verifica escuchándola, y hasta el 2026-09-04 «Ver crudo» era la única
                    evidencia que ofrecía — o sea, ninguna para un audio que falló al transcribir. */}
                {audio && audio.loading && (
                    <div className="iab-src-note">{t('ia_booster.feed.ingest_listen_loading')}</div>
                )}
                {audio && audio.url && (
                    <div className="iab-src-player">
                        <AudioNotePlayer src={audio.url}
                                         durationSeconds={doc.file && doc.file.duration_seconds}
                                         label={doc.title}/>
                    </div>
                )}
                {audio && audio.error && (
                    <div className="iab-src-note" role="alert">
                        {t('ia_booster.feed.ingest_listen_error')}
                    </div>
                )}
            </div>
            <div className="iab-src-acts">
                {/* Si preparar el audio falló, el botón vuelve: el error puede ser transitorio y sin
                    esto «Escuchar» desaparecía de esa fila para el resto de la visita. */}
                {isAudio && (!audio || audio.error) && (
                    <button type="button" className="iab-src-act" onClick={() => onListen(doc)}>
                        {t('ia_booster.feed.ingest_listen')}
                    </button>
                )}
                {doc.has_raw && (
                    <button type="button" className="iab-src-act" onClick={() => onViewRaw(doc)}>
                        {isAudio
                            ? t('ia_booster.feed.ingest_read_transcript')
                            : t('ia_booster.sources.view_content')}
                    </button>
                )}
                {/* `can_retry === false` = el backend ya sabe que reintentar da lo mismo. Ofrecerlo
                    igual es prometer algo que no puede funcionar. */}
                {failed && doc.can_retry !== false && (
                    <button type="button" className="iab-src-act" disabled={busy}
                            onClick={() => onRetry(doc)}>
                        {t('client_memory.documents.retry')}
                    </button>
                )}
                {/* «Actualizar» a la vista — decisión de fede. Drive igual se relee solo todos los
                    días (el botón es para no esperar, y lo dice su ⓘ); una página web, en cambio,
                    sólo se vuelve a leer con este botón. Nunca conviven: un documento viene de una
                    fuente o de la otra, y `driveKeyOf` sólo casa documentos cuya fuente ES Drive.
                    Con el acceso perdido no se ofrece: se ofrece reconectar. */}
                {isDrive && !failed && !driveBroken && (
                    <DriveRefreshButton syncing={syncing} onSync={onSyncConnection}/>
                )}
                {isDrive && driveBroken && canConnect && (
                    <button type="button" className="iab-src-act" onClick={onReconnect}>
                        {t('client_memory.connections.reconnect')}
                    </button>
                )}
                {isUrlDoc && !flying && (
                    <button type="button" className="iab-src-act" disabled={busy}
                            onClick={() => onRefreshUrl(doc)}>
                        {busy
                            ? t('client_memory.documents.refresh_url_busy')
                            : t('ia_booster.sources.refresh')}
                    </button>
                )}
                {ready && doc.can_reprocess && (
                    <button type="button" className="iab-src-act" disabled={busy}
                            onClick={() => onReprocess(doc)}>
                        {t('client_memory.documents.reprocess')}
                    </button>
                )}
                <button type="button" className="iab-src-act -danger" onClick={() => onDelete(doc)}>
                    {t('client_memory.action.delete')}
                </button>
            </div>
        </li>
    );
}

export default SourceRow;
